/**
 * @file BackupInstallSection.tsx
 * @description «Установка на сервере» (phase19.26 шаг 7; шаг 7.1): папка для
 * копий и одноразовая команда `curl … | sudo bash`, которая ставит агента
 * бэкапа (программу на сервере, которая делает копии) и подключает его к панели.
 *
 * Команда содержит установочный ключ — действует час и срабатывает один раз,
 * поэтому получаем её только по кнопке, не храним и убираем с экрана по
 * истечении срока или после экстренного отзыва ключа агента (`revokeNonce`:
 * команда, полученная до отзыва, погашена сервером). Папка копий задаётся
 * здесь (Р1): сменить её = выполнить установку заново с новой папкой. Пока
 * поле не правили, в нём папка из отчёта агента — и когда отчёт пришёл позже.
 */

import { Terminal } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { EditorialCard } from "../../ui/EditorialCard";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  backupErrorMessage,
  createBackupInstallCommand,
  type BackupInstallCommand,
} from "../../../lib/api-backups";
import { DEFAULT_BACKUP_ROOT, backupRootError, installFolderFrom, installMsLeft } from "./backup-install";
import { BackupInstallCommandBox } from "./BackupInstallCommandBox";

interface Props {
  /** Папка копий по отчёту агента — ею заполняем поле, чтобы переустановка не переносила копии. */
  localRoot: string | null;
  /** Ключ агента отозван в этой сессии, и агент с тех пор не отчитывался. */
  agentRevoked: boolean;
  /** Счётчик экстренных отзывов: команда, полученная до отзыва, недействительна. */
  revokeNonce: number;
}

/** Команда + номер отзыва, при котором её получили. */
type IssuedCommand = BackupInstallCommand & { nonce: number };

export function BackupInstallSection({ localRoot, agentRevoked, revokeNonce }: Props) {
  const notify = useNotify();
  // null — поле не правили: показываем папку из отчёта агента (она может прийти позже).
  const [editedFolder, setEditedFolder] = useState<string | null>(null);
  const folder = editedFolder ?? installFolderFrom(localRoot);
  const [serverFolderError, setServerFolderError] = useState<string | null>(null);
  const [install, setInstall] = useState<IssuedCommand | null>(null);
  const [expired, setExpired] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  // Ключ агента отозван после получения команды — сервер её погасил, убираем.
  if (install && install.nonce !== revokeNonce) setInstall(null);

  // Обратный отсчёт: по истечении срока команда уходит из state и с экрана.
  useEffect(() => {
    if (!install) return undefined;
    const timer = window.setInterval(() => {
      const current = Date.now();
      if (installMsLeft(install.expiresAt, current) <= 0) {
        setInstall(null);
        setExpired(true);
      } else {
        setNow(current);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [install]);

  const folderError = backupRootError(folder) ?? serverFolderError;

  async function load() {
    const problem = backupRootError(folder);
    if (problem) {
      notify.error(`Папка для копий: ${problem}`);
      return;
    }
    setBusy(true);
    try {
      const issued = await createBackupInstallCommand(folder.trim());
      setNow(Date.now());
      setExpired(false);
      setInstall({ ...issued, nonce: revokeNonce });
    } catch (err) {
      // 400 — папку не принял сервер: показываем под полем.
      if (err instanceof ApiError && err.status === 400) setServerFolderError(err.message);
      else notify.error(backupErrorMessage(err, "Не удалось получить команду установки"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Установка на сервере"
        subtitle="Агент — программа на сервере приложения, которая делает копии и отчитывается панели"
      />
      <EditorialCard.Body>
        <div className="flex flex-col gap-4">
          {agentRevoked ? (
            <NoticeStrip tone="warning" title="Ключ отозван">
              Панель не видит агента — получите команду установки и выполните её на сервере
              приложения. До этого копии продолжают делаться с настройками по умолчанию, но
              в истории не появятся.
            </NoticeStrip>
          ) : null}

          <p className="text-sm text-on-surface-variant">
            Выполните команду на сервере приложения от имени администратора (root): она установит
            агента и подключит его к этой панели. Для переезда на новый сервер — та же команда.
          </p>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bk-install-root">Папка для копий на сервере</Label>
            <Input
              id="bk-install-root"
              value={folder}
              onChange={(e) => {
                setEditedFolder(e.target.value);
                setServerFolderError(null);
              }}
              placeholder={DEFAULT_BACKUP_ROOT}
              spellCheck={false}
              autoCapitalize="off"
              aria-invalid={folderError ? true : undefined}
              className="font-mono"
              disabled={busy}
            />
            <span className={folderError ? "text-xs text-destructive" : "text-xs text-on-surface-variant"}>
              {folderError ??
                "Только внутри /srv, /mnt или /backup. Сменить папку позже = выполнить установку заново с новой папкой; старые копии останутся в прежней."}
            </span>
          </div>

          {install ? <BackupInstallCommandBox install={install} now={now} /> : null}
          {!install && expired ? (
            <NoticeStrip tone="info">Срок команды истёк — получите новую.</NoticeStrip>
          ) : null}

          <Button
            variant={install ? "outline" : "default"}
            className="sm:self-start"
            onClick={() => void load()}
            loading={busy}
            disabled={Boolean(backupRootError(folder))}
          >
            <Terminal className="size-4" />
            {busy ? "Получаем…" : install ? "Получить новую команду" : "Получить команду установки"}
          </Button>
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
